/**
 * VisibilityScopeSelector — explicit Spanish picker for the visibility
 * scope of a document or construction report.
 *
 * Used by:
 *   - Operaciones DocumentosTab
 *   - Operaciones ReportesTab
 *   - Admin Data Room (standalone DocumentsPage)
 *
 * Each option pairs the canonical key with a friendly Spanish label and a
 * helper string. project_all renders an extra confirmation checkbox; the
 * parent form must NOT submit until `confirmProjectAll` is true.
 */
import React from "react";

export const SCOPE_OPTIONS = [
  {
    key: "internal",
    label: "Solo equipo interno",
    help: "No visible para inversores.",
  },
  {
    key: "investor",
    label: "Solo este inversor",
    help: "El inversor seleccionado podrá verlo.",
  },
  {
    key: "villa",
    label: "Inversores de esta villa",
    help: "Todos los inversores con propiedad o pago en la villa.",
  },
  {
    key: "investor_all_villas",
    label: "Este inversor, todas sus villas",
    help: "El inversor seleccionado lo verá en todo su portfolio.",
  },
  {
    key: "project_all",
    label: "Todas las villas / proyecto",
    help: "Visible para todos los inversores activos. Requiere confirmación.",
  },
];

export const SCOPE_LABEL = Object.fromEntries(
  SCOPE_OPTIONS.map((o) => [o.key, o.label]),
);

export const SCOPE_TONE = {
  internal:            "bg-stone-100 text-stone-700 border-stone-200",
  investor:            "bg-blue-50 text-blue-800 border-blue-200",
  villa:               "bg-cyan-50 text-cyan-800 border-cyan-200",
  investor_all_villas: "bg-violet-50 text-violet-800 border-violet-200",
  project_all:         "bg-amber-50 text-amber-900 border-amber-300",
};

/**
 * Render the scope chip for a given row. Legacy rows (no visibility_scope)
 * get a distinctive "Heredado — revisar" amber chip so admins notice them.
 */
export function ScopeChip({ scope, testid }) {
  if (!scope) {
    return (
      <span
        className="inline-flex text-[10px] px-2 py-0.5 rounded-full border bg-amber-50 text-amber-900 border-amber-300"
        data-testid={testid || "scope-chip-legacy"}
        title="Documento o reporte heredado. Asigna un alcance explícito."
      >
        Heredado — revisar
      </span>
    );
  }
  const tone = SCOPE_TONE[scope] || SCOPE_TONE.internal;
  return (
    <span
      className={`inline-flex text-[10px] px-2 py-0.5 rounded-full border ${tone}`}
      data-testid={testid || `scope-chip-${scope}`}
    >
      {SCOPE_LABEL[scope] || scope}
    </span>
  );
}

/**
 * Controlled component.
 *
 * Props:
 *   value                — current scope key (e.g. "internal")
 *   onChange(scope)      — fired when the user picks a different scope
 *   confirmProjectAll    — boolean state for the project_all confirmation
 *   onConfirmProjectAll  — setter for the confirmation
 *   availableScopes      — optional whitelist (default: all 5)
 *   testidPrefix         — base prefix for data-testids
 */
export default function VisibilityScopeSelector({
  value,
  onChange,
  confirmProjectAll = false,
  onConfirmProjectAll,
  availableScopes,
  testidPrefix = "visibility",
}) {
  const opts = availableScopes
    ? SCOPE_OPTIONS.filter((o) => availableScopes.includes(o.key))
    : SCOPE_OPTIONS;
  const current = opts.find((o) => o.key === value);

  return (
    <div className="space-y-2" data-testid={`${testidPrefix}-scope`}>
      <label className="block text-xs text-stone-500">
        Alcance / visibilidad
        <select
          className="dv-input w-full mt-1 text-sm"
          value={value || ""}
          onChange={(e) => onChange(e.target.value || null)}
          data-testid={`${testidPrefix}-scope-select`}
        >
          <option value="">— Selecciona —</option>
          {opts.map((o) => (
            <option key={o.key} value={o.key}>{o.label}</option>
          ))}
        </select>
      </label>
      {current && (
        <div className="text-[11px] text-stone-500">{current.help}</div>
      )}
      {value === "project_all" && (
        <label className="flex items-start gap-2 text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
          <input
            type="checkbox"
            checked={!!confirmProjectAll}
            onChange={(e) => onConfirmProjectAll && onConfirmProjectAll(e.target.checked)}
            className="mt-0.5"
            data-testid={`${testidPrefix}-scope-confirm`}
          />
          <span>
            Confirmo que este contenido será visible para todos los inversores.
          </span>
        </label>
      )}
    </div>
  );
}
